import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable, DataTableColumn, SortState } from '../patterns';
import {
  getDaysSinceDate,
  getFreshnessStatus,
  FreshnessThresholds,
} from '../../utils/scoutingUtils';
import { naturalCompare } from '../../utils/naturalCompare';

export interface FreshnessRow {
  orchardId: string;
  orchardName: string;
  farmName: string;
  lastScoutedDate: string | null;
  lastScouterName: string | null;
}

interface Props {
  rows: FreshnessRow[];
  thresholds: FreshnessThresholds;
  viewAllHref?: string;
  onRowHover?: (orchardId: string | null) => void;
  onRowClick?: (row: FreshnessRow) => void;
}

export const ScoutingFreshnessCard: React.FC<Props> = ({ rows, thresholds, viewAllHref, onRowHover, onRowClick }) => {
  const { t } = useTranslation();
  const [sort, setSort] = useState<SortState>({ key: 'daysSince', direction: 'desc' });

  const enriched = rows.map(r => {
    const days = getDaysSinceDate(r.lastScoutedDate);
    return { ...r, daysSince: days, status: getFreshnessStatus(days, thresholds) };
  });

  const sorted = [...enriched].sort((a, b) => {
    if (sort.key === 'orchardName') {
      const dir = sort.direction === 'asc' ? 1 : -1;
      return dir * naturalCompare(a.orchardName, b.orchardName);
    }
    if (sort.key === 'daysSince') {
      const da = a.daysSince ?? Number.POSITIVE_INFINITY;
      const db = b.daysSince ?? Number.POSITIVE_INFINITY;
      const diff = sort.direction === 'desc' ? db - da : da - db;
      if (diff !== 0) return diff;
      return naturalCompare(a.orchardName, b.orchardName);
    }
    return 0;
  });

  const columns: DataTableColumn<typeof enriched[number]>[] = [
    { key: 'status', label: t('dashboard.status'), render: (r) => <span className={`scouting-freshness__pip scouting-freshness__pip--${r.status}`} aria-label={r.status} /> },
    { key: 'orchardName', label: t('dashboard.orchardHeader'), sortable: true, render: (r) => r.orchardName },
    { key: 'farmName', label: t('dashboard.farmHeader'), render: (r) => r.farmName },
    {
      key: 'daysSince',
      label: t('dashboard.daysAgo'),
      sortable: true,
      align: 'right',
      render: (r) => r.daysSince === null ? t('dashboard.never') : t('dashboard.days', { count: r.daysSince }),
    },
    { key: 'scouter', label: t('dashboard.scouter'), render: (r) => r.lastScouterName ?? '—' },
  ];

  const onMouseOver = (e: React.MouseEvent) => {
    if (!onRowHover) return;
    const tr = (e.target as HTMLElement).closest('tr[data-rowkey]');
    if (tr) onRowHover((tr as HTMLElement).dataset.rowkey ?? null);
  };
  const onMouseLeave = () => onRowHover?.(null);

  return (
    <section
      className="scouting-freshness-card"
      onMouseOver={onRowHover ? onMouseOver : undefined}
      onMouseLeave={onRowHover ? onMouseLeave : undefined}
    >
      <header className="scouting-freshness-card__header">
        <h3 className="pat-section-header">{t('dashboard.freshnessTitle')}</h3>
        {viewAllHref && (
          <a className="scouting-freshness-card__view-all" href={viewAllHref}>
            {t('dashboard.scouting.viewAll')}
          </a>
        )}
      </header>
      <DataTable
        columns={columns}
        rows={sorted}
        rowKey={(r) => r.orchardId}
        sort={sort}
        onSortChange={setSort}
        onRowClick={onRowClick}
      />
    </section>
  );
};
